import { Outlet, useLocation } from "react-router";
import { useEffect } from "react";
import { motion } from "framer-motion";
import Navbar from "@/components/ui/Navbar";
import pages from "@/lib/public-pages.json";

/**
 * Layout for the public site. The navbar is rendered once here so it persists
 * across navigation (no blink). The page content fades in on mount via a
 * key={pathname} wrapper — a plain enter-only transition with NO AnimatePresence
 * and NO useOutlet, so there's no exit-animation race that can wedge navigation.
 */
export default function SiteLayout() {
  const location = useLocation();
  const isHome = location.pathname === "/";
  useEffect(() => {
    const page = pages.find(page => page.path === location.pathname)
      ?? { ...pages[0], path: location.pathname, title: "A Few Loose Threads" };
    // Guide detail pages manage their own titles once their content loads.
    if (!location.pathname.startsWith("/guides/")) document.title = page.title;
    document.querySelector('meta[name="description"]')?.setAttribute("content", page.description);
    const url = `https://afewloosethreads.com${page.path}`;
    document.querySelector('link[rel="canonical"]')?.setAttribute("href", url);
    for (const [selector, value] of [
      ['meta[property="og:url"]', url],
      ['meta[property="og:title"]', page.title],
      ['meta[property="og:description"]', page.description],
      ['meta[property="og:image"]', `https://afewloosethreads.com${page.image ?? "/og-image.png"}`],
      ['meta[property="og:image:alt"]', page.imageAlt ?? "Chelsea Porter, sewist and creator behind A Few Loose Threads"],
      ['meta[property="og:image:width"]', page.image ? "1733" : "2400"],
      ['meta[property="og:image:height"]', page.image ? "907" : "1260"],
      ['meta[name="twitter:title"]', page.title],
      ['meta[name="twitter:description"]', page.description],
      ['meta[name="twitter:image"]', `https://afewloosethreads.com${page.image ?? "/og-image.png"}`],
      ['meta[name="twitter:image:alt"]', page.imageAlt ?? "Chelsea Porter, sewist and creator behind A Few Loose Threads"],
    ]) document.querySelector(selector)?.setAttribute("content", value);
  }, [location.pathname]);
  return (
    <>
      <Navbar sticky={isHome} dark={location.pathname === "/events/stitches-night-out"} />
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2, ease: "easeInOut" }}
      >
        <Outlet />
      </motion.div>
    </>
  );
}
